import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";
import { useFragment } from "react-relay";

import { isAlreadyPaidAtom } from "@/screens/BookingDetail/NewPatronBookingFlow/components/EditBooking/atoms/selectors/isAlreadyPaidAtom/isAlreadyPaidAtom";
import {
  DropdownSelect,
  DropdownSelectOption,
} from "@whoosh/design-system/src/components/controls";
import { GetCourseLayoutIconFromCode } from "@whoosh/design-system/src/components/icons_v2/courseLayout";
import { VStack } from "@whoosh/design-system/src/components/primitive";

import { setCourseLayoutOnRoundAtom } from "../../../../../../../../../../atoms/actions/setCourseLayoutOnRound/setCourseLayoutOnRoundAtom";
import { getCourseLayoutFromRoundIdAtom } from "../../../../../../../../../../atoms/selectors/getCourseLayoutFromRoundIdAtom/getCourseLayoutFromRoundIdAtom";
import { CourseLayoutControl_Booking$key } from "./__generated__/CourseLayoutControl_Booking.graphql";

type Props = {
  bookingRef: CourseLayoutControl_Booking$key;
  roundId: string;
};
export const CourseLayoutControl = ({ bookingRef, roundId }: Props) => {
  const booking = useFragment(CourseLayoutControlFragmentString, bookingRef);
  const selectedCourseLayout = useAtomValue(
    getCourseLayoutFromRoundIdAtom(roundId)
  );

  const [isTargetingAll, setIsTargetingAll] = useState(false);
  const setCourseLayoutOnRound = useSetAtom(setCourseLayoutOnRoundAtom);
  const permittedCourseLayouts =
    booking.startSlot?.permittedCourseLayouts?.edges?.map((e) => e?.node) ??
    booking.startSlot?.agenda?.facility?.courseLayouts?.edges?.map(
      (e) => e?.node
    );
  const dropdownOptions: DropdownSelectOption<string>[] =
    permittedCourseLayouts?.flatMap((courseLayout) => {
      if (!courseLayout?.id) return [];
      const icon = GetCourseLayoutIconFromCode(
        courseLayout?.holeCount || courseLayout?.code
      );
      return {
        value: courseLayout.id,
        leftElement: icon ? icon({}) : undefined,
        triggerElement: icon ? icon({}) : undefined,
        label: "",
      };
    }) ?? [];

  // Check if selectedCourseLayout id matches any dropdown option value
  const isSelectedCourseLayoutInDropdown =
    !!selectedCourseLayout &&
    dropdownOptions.some((option) => option.value === selectedCourseLayout.id);

  const isAlreadyPaid = useAtomValue(isAlreadyPaidAtom);

  return (
    <VStack width={48}>
      <DropdownSelect
        hideOuterMargin
        includeLeftElementInSelectedValue
        hideChevron
        hideLabelInDisplay
        hideSubLabelInDisplay
        contentSizeOverride="regular"
        optionalToggle={{
          initiallyChecked: isTargetingAll,
          onToggle: setIsTargetingAll,
        }}
        isViewOnly={isAlreadyPaid}
        options={dropdownOptions}
        value={
          isSelectedCourseLayoutInDropdown
            ? selectedCourseLayout?.id
            : dropdownOptions[0]?.value
        }
        onChange={(courseLayoutId) => {
          const matchingCourseLayout = permittedCourseLayouts?.find(
            (courseLayout) => courseLayout?.id === courseLayoutId
          );
          if (matchingCourseLayout) {
            setCourseLayoutOnRound({
              roundId,
              courseLayout: {
                id: matchingCourseLayout.id,
                holeCount: matchingCourseLayout.holeCount,
              },
              isTargetingAll,
            });
          }
        }}
      />
    </VStack>
  );
};

const CourseLayoutControlFragmentString = graphql`
  fragment CourseLayoutControl_Booking on Booking {
    startSlot {
      ... on TimeSlot {
        permittedCourseLayouts(memberVisible: true) {
          edges {
            node {
              id
              holeCount
              code
            }
          }
        }
      }
      ... on QueueSlot {
        agenda {
          facility {
            courseLayouts {
              edges {
                node {
                  id
                  holeCount
                  code
                }
              }
            }
          }
        }
      }
    }
  }
`;
